import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useCallback, useEffect, createElement, useContext, createContext } from 'react';
import { v4 } from 'uuid';
import { AnimatePresence, motion } from 'framer-motion';
import { Toast } from './Toast.js';

var _excluded = ["children"];
var DEFAULT_AUTO_CLOSE_TIMEOUT = 7500;
var ToastContext = /*#__PURE__*/createContext({});

var insertToastWithoutDuplicates = function insertToastWithoutDuplicates(currentToasts, id, content, options, close) {
  var nextToasts = [].concat(currentToasts);
  var existingToastIndex = currentToasts.findIndex(function (toast) {
    return JSON.stringify(toast.content) === JSON.stringify(content);
  });

  if (existingToastIndex > -1) {
    nextToasts[existingToastIndex].lastDuplicateId = id;
    nextToasts[existingToastIndex].lastUpdated = Date.now();
  } else {
    nextToasts.push({
      id: id,
      content: typeof content === 'function' ? content(close) : content,
      options: options
    });
  }

  return nextToasts;
};

var ToastProvider = function ToastProvider(_ref) {
  var children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _React$useState = useState([]),
      toasts = _React$useState[0],
      setToasts = _React$useState[1];

  var handleClose = function handleClose(id) {
    setToasts(function (currentToasts) {
      return currentToasts.filter(function (toast) {
        return toast.id !== id;
      });
    });
  }; // memoize (useCallback) this function,
  // it is the value of the context provider and we don't want it to trigger state tree re-renders on provider children


  var toaster = useCallback(function (content, options) {
    var id = v4();

    var close = function close() {
      return handleClose(id);
    };

    setToasts(function (currentToasts) {
      return insertToastWithoutDuplicates(currentToasts, id, content, options, close);
    });

    var update = function update(content, options) {
      setToasts(function (currentToasts) {
        var nextToasts = currentToasts.filter(function (toast) {
          if (toast.lastDuplicateId) {
            return toast.lastDuplicateId !== id;
          }

          return toast.id !== id;
        });
        return insertToastWithoutDuplicates(nextToasts, v4(), content, options, close);
      });
    };

    var success = function success(content, options) {
      update(content, _extends({
        autoClose: DEFAULT_AUTO_CLOSE_TIMEOUT
      }, options, {
        type: 'success'
      }));
    };

    var error = function error(content, options) {
      update(content, _extends({}, options, {
        type: 'error'
      }));
    };

    var warning = function warning(content, options) {
      update(content, _extends({}, options, {
        type: 'warning'
      }));
    };

    var information = function information(content, options) {
      update(content, _extends({}, options, {
        type: 'information'
      }));
    };

    var loading = function loading(content, options) {
      update(content, _extends({}, options, {
        type: 'loading'
      }));
    };

    return {
      success: success,
      error: error,
      warning: warning,
      information: information,
      loading: loading,
      close: close
    };
  }, []); // no need to rebind these every render, do them once in an effect

  useEffect(function () {
    toaster.success = function (content, options) {
      return toaster(content, _extends({
        autoClose: DEFAULT_AUTO_CLOSE_TIMEOUT
      }, options, {
        type: 'success'
      }));
    };

    toaster.error = function (content, options) {
      return toaster(content, _extends({}, options, {
        type: 'error'
      }));
    };

    toaster.warning = function (content, options) {
      return toaster(content, _extends({}, options, {
        type: 'warning'
      }));
    };

    toaster.information = function (content, options) {
      return toaster(content, _extends({}, options, {
        type: 'information'
      }));
    };

    toaster.loading = function (content, options) {
      return toaster(content, _extends({}, options, {
        type: 'loading'
      }));
    };
  }, []);
  return createElement(ToastContext.Provider, Object.assign({}, props, {
    value: toaster
  }), children, createElement("div", {
    id: "yt-toast__container",
    role: "log"
  }, createElement(AnimatePresence, {
    initial: false
  }, toasts.map(function (toast) {
    return createElement(motion.div, {
      key: toast.id,
      layoutTransition: {
        type: 'spring',
        damping: 20,
        stiffness: 300
      },
      initial: {
        opacity: 0,
        y: 10,
        scale: 0.5
      },
      animate: {
        opacity: 1,
        y: 0,
        scale: 1
      },
      exit: {
        opacity: 0,
        scale: 0.5,
        transition: {
          duration: 0.2
        }
      }
    }, createElement(Toast, Object.assign({}, toast, {
      onClose: function onClose() {
        return handleClose(toast.id);
      }
    })));
  }))));
};
var useToast = function useToast() {
  return useContext(ToastContext);
};

export { ToastProvider, useToast };
//# sourceMappingURL=Toaster.js.map
